'use client';

import Image from 'next/image';
import { Modal, ModalContent } from 'ui/components/ModalV2/Modal';
import { Trans, useTranslation } from '@whoop/i18n/lang/client';
import { TERMS_OF_USE_URL } from 'ui';
import { useShallow } from 'zustand/react/shallow';
import { Button } from 'ui/components/ButtonV2/Button';
import { getFormatters } from 'ui/hooks/formatter';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { GOOSE_WHATS_INCLUDED } from '@/lib/constants/images';
export interface LearnMoreModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleChooseTrial: () => Promise<void>;
  disabled: boolean;
  renewalPrice: number;
}
export default function LearnMoreModal({
  isOpen,
  setIsOpen,
  handleChooseTrial,
  disabled = false,
  renewalPrice
}: LearnMoreModalProps) {
  const {
    t
  } = useTranslation('learnMoreModal');

  // TODO: get this content from contentful
  const {
    currency,
    language,
    region,
    returnShippingCost
  } = useSiteStore(useShallow(state => ({
    currency: state.currency,
    language: state.language,
    region: state.region,
    returnShippingCost: state.returnShippingCost
  })));
  const {
    formatDisplayPrice
  } = getFormatters({
    currency,
    language,
    region
  });
  const handleTrialSelection = async (): Promise<void> => {
    await handleChooseTrial();
    setIsOpen(false);
  };
  const annualPrice = formatDisplayPrice(renewalPrice);
  const returnShippingFormattedPrice = returnShippingCost ? formatDisplayPrice(returnShippingCost) : null;
  const renewalPolicy = returnShippingFormattedPrice ? 'renewalPolicyReturnShipping' : 'renewalPolicy';
  return <Modal onOpenChange={setIsOpen} open={isOpen} data-sentry-element="Modal" data-sentry-component="LearnMoreModal" data-sentry-source-file="LearnMoreModal.tsx">
      <ModalContent className='z-modal xs:rounded-2xl max-h-[100dvh] overflow-y-scroll sm:overflow-y-auto md:max-h-[80vh] md:min-w-[600px] md:rounded-3xl' data-sentry-element="ModalContent" data-sentry-source-file="LearnMoreModal.tsx">
        <div className='grid max-w-xl grid-cols-1 gap-2 pb-5 text-center md:px-3' data-testid='learn-more-modal'>
          <h2 className='text-[28px] font-semibold'>{t('title')}</h2>
          <div>
            <p className='py-0 font-medium leading-snug'>
              {t('whatsIncluded')}
            </p>
            <div className='py-3'>
              <Image alt={`WHOOP 5.0 What's In The Box?`} data-testid='learn-more-modal-image' className='m-auto rounded-3xl' height={300} src={GOOSE_WHATS_INCLUDED} width={345} data-sentry-element="Image" data-sentry-source-file="LearnMoreModal.tsx" />
            </div>
            <p className='py-1 font-medium leading-snug'>
              {t(`${renewalPolicy}`, {
              annualPrice,
              returnShippingCost: returnShippingFormattedPrice
            })}
            </p>
          </div>
          <div className='pb-3 font-medium'>
            <Trans components={[
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a className='text-blue-strain underline' href={TERMS_OF_USE_URL} key='tos-link' rel='noopener' target='_blank' />]} i18nKey='learnMoreModal:termsAndConditions' data-sentry-element="Trans" data-sentry-source-file="LearnMoreModal.tsx" />
          </div>
          <div className='flex w-full justify-center'>
            <Button className='w-full rounded-2xl' disabled={disabled} onClick={handleTrialSelection} size='medium' variant='secondary' data-sentry-element="Button" data-sentry-source-file="LearnMoreModal.tsx">
              {t('startFreeTrial')}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>;
}